import React from 'react';

import {
  Iframe,
  DescriptionList,
  DescriptionItem,
  Stack,
  Section,
  Typography,
  Link,
} from '@gogame/components';
import {
  useCommentInvoice,
  useUndoInvoice,
  useMarkAsPaidInvoice,
  useReplaceInvoice,
} from 'scenes/FinancialResults/data/mutations';
import { downloadInvoice } from 'scenes/FinancialResults/sources/financialResults';
import { defaultBaseURL } from 'api';
import Comment from './Comment';
import { CommentForm } from './CommentForm';
import InvoiceActions from './InvoiceActions';
import InvoiceStatus from './InvoiceStatus';

const Invoice = ({ data, accessToken, initialOpen = false }) => {
  const [comment] = useCommentInvoice();
  const [undo] = useUndoInvoice();
  const [markAsPaid] = useMarkAsPaidInvoice();
  const [replaceInvoiceAttachment] = useReplaceInvoice();

  const handleComment = async (_, message) => {
    await comment({
      comment: message,
      id: data.id,
    });
  };

  const handleDelete = async () => {
    await comment({
      comment: '',
      id: data.id,
    });
  };

  const handleUndo = async () => {
    await undo({
      id: data.id,
    });
  };

  const handleMarkAsPaid = async () => {
    await markAsPaid({
      id: data.id,
    });
  };

  const handleExport = async () => {
    await downloadInvoice({
      id: data.id,
    });
  };

  const handleReplaceFile = async () => {
    await replaceInvoiceAttachment({
      id: data.id,
    });
  };

  const canComment = data?.actions?.includes('comment');
  const isPDF = (data?.attachment?.name || '').toLowerCase().endsWith('.pdf');

  return (
    <Section
      title={data.attachment.name}
      subTitle={` ID ${data?.id}${
        data?.externalId ? ` | SAP ID ${data?.externalId}` : ''
      }`}
      fullHeight={true}
      isCollapsable={true}
      initialOpen={initialOpen}
      action={
        <InvoiceActions
          data={data}
          actions={data?.actions}
          onUndo={handleUndo}
          onMarkAsPaid={handleMarkAsPaid}
          onExport={handleExport}
          onReplaceFile={handleReplaceFile}
        />
      }
    >
      <Stack spacing={2}>
        <DescriptionList stripEmpty={true} data={data} disableGutters={true}>
          <DescriptionItem field={'type.name'} label={'Type'} />
          <DescriptionItem
            field={'spending.currencyCode'}
            label={'Currency'}
            valueParser={(v) => v?.toUpperCase()}
          />
          <DescriptionItem
            field={'status'}
            label={'Status'}
            type={({ value }) => (
              <InvoiceStatus id={value.id}>{value.name}</InvoiceStatus>
            )}
          />
          <DescriptionItem
            field={'amount'}
            label={'Invoice Amount'}
            type={'amount'}
          />
          <DescriptionItem
            field={'spending'}
            label={'GG Spend'}
            type={'amount'}
          />
        </DescriptionList>
        {data?.comment ? (
          <Comment
            message={data?.comment}
            onComment={handleComment}
            onDelete={handleDelete}
            withActions={canComment}
          />
        ) : canComment ? (
          <CommentForm onChange={handleComment} />
        ) : null}
        {accessToken &&
          (isPDF ? (
            <Iframe
              src={`${defaultBaseURL}/api/financial-results/invoices/${data.id}/preview?access_token=${accessToken}&attachmentId=${data.attachment.id}`}
              width={'100%'}
              height={'calc(100vh - 192px)'}
              style={{ paddingTop: '16px' }}
            />
          ) : (
            <Typography noWrap={true}>
              <Link
                href={`${defaultBaseURL}/api/financial-results/invoices/${data.id}/download?access_token=${accessToken}`}
                target={'_blank'}
              >
                {data.name}
              </Link>
            </Typography>
          ))}
      </Stack>
    </Section>
  );
};

export default Invoice;
